/** @jsx jsx */
import { jsx } from "frontend/src/utils/jsx"
import Link from "gatsby-link"
import { useCallback } from "react"

// set of styles to provide visual feedback for focus, hovering and having the elemtn pressed down
const opacity = {
  transition: "all 0.1s linear",
  ":focus": { outline: "none", opacity: "0.9" },
  ":active": { opacity: "0.8" },
  ":hover": { opacity: 0.6 },
}

// set of styles to remove the defaults from <a> components
const linkReset = {
  textDecoration: "none",
  color: "currentColor",
  position: "relative",
  flexShrink: 0,
}

// set of styles to remove the defaults from <button> components
const ButtonReset = {
  background: "transparent",
  color: "currentColor",
  border: "none",
  textAlign: "left",
  padding: 0,
  cursor: "pointer",
}

// the reason to set these to undefined as it that they are all optional
function Touchable({
  // can be button or link, selects the appropriate html element, or non-interactive to turn the element into a normal wrapper that ignores interactive props
  role = "button",
  children = null,
  onClick = undefined,
  // if this is provided, it should be a normal external link
  href = undefined,
  // internal links
  to = undefined,
  state = {},
  // can be feedback or none, disables all visual styling
  feedback = "opacity",
  disabled = false,
  // this is a way to pass all other props to the underlying element, often useful when creating components to replace/wrap native dom elements
  ...rest
}) {
  // wrap the provided onClick handler to always add preventing the default on link clicks

  const withPrevent = useCallback(
    e => {
      e.stopPropagation()
      e.preventDefault()
      onClick && onClick(e)
    },
    [onClick]
  )

  // select the component to use for links, has to be different for internal and externral links
  const LinkComp = to ? Link : "a"
  // select the set of styles to add for interaction feedback
  const feedbackStyle = feedback !== "none" ? opacity : {}
  // select if the component should be link/<a> or <button>, if it has to, href, toRelative it needs to always be a link
  if (role === "link" || href || to) {
    if (disabled) {
      return (
        <button
          sx={{ ...ButtonReset, cursor: "default !important", opacity: disabled ? 0.3 : 1 }}
          type="button"
          disabled={disabled}
          {...rest}
        >
          {children}
        </button>
      )
    }
    return (
      <LinkComp
        // combine the reset styles and the selected feedback styles (the theme-ui jsx will also combine in the sx put on the Touchable component)
        sx={{ ...linkReset, ...feedbackStyle, opacity: disabled ? 0.3 : 1 }}
        // use # for non external links, otherwise they are not valid
        href={href || "#"}
        // select if the link should be from the root or from the sectino root, if both are undefined it will be undefined which is fine
        to={to}
        options={{ state }}
        // if the link is actually used as a link, the onClick shouldn't prevent the default
        onClick={!disabled && onClick && (to || href ? onClick : withPrevent)}
        // pass on all other props passed to <Touchable/>
        {...rest}
      >
        {children}
      </LinkComp>
    )
  } else {
    return (
      // the button is much simpler because it can not do anything other than run its onClick
      <button
        sx={{ ...ButtonReset, ...feedbackStyle, opacity: disabled ? 0.3 : 1 }}
        type="button"
        onClick={withPrevent}
        disabled={disabled}
        {...rest}
      >
        {children}
      </button>
    )
  }
}

export { Touchable }
